import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Star, Quote, Play, Pause, Volume2 } from 'lucide-react';
const testimonials = [{
  name: 'María G.',
  location: 'CDMX',
  image: 'https://randomuser.me/api/portraits/women/89.jpg',
  text: 'Sentía mucha angustia por la inseguridad en mi colonia. Empecé a usar las oraciones de sellamiento en las puertas de mi casa y ahora mis hijos y yo dormimos con una paz que no puedo explicar. Es la mejor inversión que he hecho.'
}, {
  name: 'Carlos M.',
  location: 'Monterrey',
  image: 'https://randomuser.me/api/portraits/men/78.jpg',
  text: 'Como padre de familia, me sentía impotente cada vez que mi hija salía a la universidad. Desde que rezamos el Escudo de Fe juntos antes de que salga, la ansiedad desapareció. Siento que realmente va protegida.'
}, {
  name: 'Patricia L.',
  location: 'Guadalajara',
  image: 'https://randomuser.me/api/portraits/women/26.jpg',
  text: 'Habíamos sufrido un asalto y el miedo no nos dejaba vivir. La Hermana Teresa es un ángel. Este manual nos devolvió la tranquilidad. Se siente una energía diferente, de verdadera protección en nuestro hogar.'
}, {
  name: 'Rosa D.',
  location: 'Puebla',
  image: 'https://randomuser.me/api/portraits/women/55.jpg',
  text: 'Lo compré con dudas, pero la paz mental que te da hacer estas oraciones específicas no tiene precio. Es fácil de seguir y realmente sientes que estás activando una barrera espiritual alrededor de tu familia.'
}];
export function SocialProofSection() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState('0:00');
  const [duration, setDuration] = useState('0:00');
  const audioRef = useRef<HTMLAudioElement>(null);
  const formatTime = (time: number) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const current = audioRef.current.currentTime;
      const total = audioRef.current.duration;
      setCurrentTime(formatTime(current));
      setProgress(current / total * 100);
    }
  };
  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(formatTime(audioRef.current.duration));
    }
  };
  const handleEnded = () => {
    setIsPlaying(false);
    setProgress(0);
    setCurrentTime('0:00');
  };
  return <section className="py-16 px-4 bg-ice-dark border-y border-gold/20" data-id="element-133">
      <div className="max-w-6xl mx-auto" data-id="element-134">
        <div className="text-center mb-16" data-id="element-135">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-blue mb-6" data-id="element-136">
            Familias que ya viven bajo el Escudo de Fe
          </h2>
          <div className="flex justify-center gap-1 text-gold mb-4" data-id="element-137">
            {[1, 2, 3, 4, 5].map(star => <Star key={star} className="w-6 h-6 fill-current" data-id="element-138" />)}
          </div>
          <p className="font-body text-lg text-navy/80" data-id="element-139">
            Más de 4,200 familias han recuperado su paz mental
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-140">
          {testimonials.map((testimonial, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: index * 0.1
        }} className="bg-white p-8 rounded-2xl shadow-md border border-gold/10 relative" data-id="element-141">
              <Quote className="absolute top-6 right-6 w-10 h-10 text-gold/20" data-id="element-142" />

              <div className="flex items-center gap-4 mb-6" data-id="element-143">
                <img src={testimonial.image} alt={testimonial.name} className="w-16 h-16 rounded-full object-cover border-2 border-gold/50" data-id="element-144" />
                <div data-id="element-145">
                  <h4 className="font-heading font-bold text-lg text-navy-dark" data-id="element-146">
                    {testimonial.name}
                  </h4>
                  <p className="font-body text-sm text-navy/60" data-id="element-147">
                    {testimonial.location}
                  </p>
                </div>
              </div>

              <p className="font-body text-navy leading-relaxed italic" data-id="element-148">
                "{testimonial.text}"
              </p>
            </motion.div>)}
        </div>

        {/* Audio Testimonial */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="bg-navy-dark rounded-2xl p-8 md:p-10 shadow-[0_0_40px_rgba(197,148,42,0.25)] border-2 border-gold/40 flex flex-col md:flex-row items-center gap-8 relative overflow-hidden" data-id="element-149">
          {/* Subtle glow background inside the card */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(197,148,42,0.1)_0%,transparent_70%)] pointer-events-none" data-id="element-150" />

          <div className="flex-shrink-0 w-full md:w-1/3 text-center md:text-left relative z-10" data-id="element-151">
            <h3 className="font-heading text-2xl font-bold text-gold-light mb-2" data-id="element-152">
              Elena L.
            </h3>
            <p className="font-body text-sm text-ice/60 mb-4" data-id="element-153">Querétaro</p>
            <div className="flex justify-center md:justify-start gap-1 text-gold mb-4" data-id="element-154">
              {[1, 2, 3, 4, 5].map(star => <Star key={star} className="w-4 h-4 fill-current" data-id="element-155" />)}
            </div>
            <p className="font-body text-ice/90 italic text-sm leading-relaxed" data-id="element-156">
              "Escucha el testimonio de Elena, quien cuenta cómo el Escudo de Fe
              protegió a su familia después de meses de vivir con miedo."
            </p>
          </div>

          <div className="w-full md:w-2/3 bg-navy rounded-xl p-6 border border-white/10 flex items-center gap-4" data-id="element-157">
            <audio ref={audioRef} src="https://cdn.magicpatterns.com/uploads/oaDfpDSShr8mugwQVeMFN3/audio madre elenea.mp4" onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onEnded={handleEnded} className="hidden" data-id="element-158" />

            <button onClick={togglePlay} className="w-14 h-14 flex-shrink-0 rounded-full bg-gradient-to-br from-gold-light to-gold-dark flex items-center justify-center text-navy-dark hover:scale-105 transition-transform shadow-[0_0_15px_rgba(197,148,42,0.4)]" aria-label={isPlaying ? 'Pausar testimonio' : 'Reproducir testimonio'} data-id="element-159">
              {isPlaying ? <Pause className="w-6 h-6 fill-current" data-id="element-160" /> : <Play className="w-6 h-6 fill-current ml-1" data-id="element-161" />}
            </button>

            <div className="flex-1 flex flex-col gap-2" data-id="element-162">
              <div className="flex items-center justify-between text-xs font-body text-ice/60" data-id="element-163">
                <span data-id="element-164">{currentTime}</span>
                <span data-id="element-165">{duration !== '0:00' ? duration : '1:14'}</span>
              </div>

              {/* Fake Waveform that fills based on progress */}
              <div className="h-8 flex items-end gap-1 overflow-hidden relative" data-id="element-166">
                {/* Background waveform */}
                <div className="absolute inset-0 flex items-end gap-1 opacity-30" data-id="element-167">
                  {[...Array(40)].map((_, i) => {
                  // Create a pseudo-random but consistent waveform pattern
                  const height = 20 + Math.sin(i * 0.5) * 30 + Math.cos(i * 0.2) * 20 + 30;
                  return <div key={`bg-${i}`} className="flex-1 rounded-t-sm bg-ice/50" style={{
                    height: `${Math.min(100, Math.max(20, height))}%`
                  }} data-id="element-168" />;
                })}
                </div>

                {/* Active waveform overlay */}
                <div className="absolute inset-y-0 left-0 flex items-end gap-1 overflow-hidden transition-all duration-100" style={{
                width: `${progress}%`
              }} data-id="element-169">
                  {[...Array(40)].map((_, i) => {
                  const height = 20 + Math.sin(i * 0.5) * 30 + Math.cos(i * 0.2) * 20 + 30;
                  return <div key={`fg-${i}`} className="flex-1 rounded-t-sm bg-gold min-w-[6px]" style={{
                    height: `${Math.min(100, Math.max(20, height))}%`
                  }} data-id="element-170" />;
                })}
                </div>
              </div>
            </div>

            <Volume2 className="w-5 h-5 text-ice/40 hidden sm:block" data-id="element-171" />
          </div>
        </motion.div>
      </div>
    </section>;
}